<template>
  <v-app-bar :elevation="0" density="compact" class="standalone-topbar">
    <!-- ── Where am I ────────────────────────────────────────────────────
         The app's identity, at the far left of the bar — the first thing
         read on every screen, and the one place it appears (the sidebar
         below carries destinations only). The open section is the lit
         sidebar row; this bar never repeats it. -->
    <div class="sh-brand">
      <button
        v-if="mobile"
        type="button"
        class="sh-nav-toggle"
        :aria-expanded="navOpen ? 'true' : 'false'"
        :aria-label="t('nav.toggle')"
        @click="toggleNav"
      >
        <Transition name="sh-icon-swap" mode="out-in">
          <v-icon :key="navOpen ? 'close' : 'menu'" size="20">{{ navOpen ? "close" : "menu" }}</v-icon>
        </Transition>
        <!-- 🔴 On a phone the "new version" pill lives in the drawer (no bottom rail there), so the way in
             says one is waiting — a fix nobody knows to load reaches nobody. -->
        <span v-if="newVersionReady && !navOpen" class="sh-nav-toggle__pip" data-testid="nav-new-version" aria-hidden="true"></span>
      </button>
      <span class="sh-brand__mark">
        <LogoTile :branding="branding" :alt="brandName" :size="26" />
      </span>
      <!-- No subtitle: Pinksy's name IS the brand (D118). A WHITE_LABEL app used to draw the
           customer's name here with the vendor's beneath it; there is one app and it is ours. -->
      <!-- 🔴 Not on a phone: the logo IS the identity there, and the width goes to the production's
           name (Devin, 2026-09-25: "on the mobile we don't need to see Pinksy Desk, just the logo").
           The name stays as the logo's alt text. -->
      <span v-if="!mobile" class="sh-brand__id" data-testid="brand-name">
        <span class="sh-brand__name">{{ brandName }}</span>
      </span>
      <!-- One glyph that TURNS rather than two that swap: the chevron points
           at what the click will do (collapse ← / expand →). -->
      <button
        v-if="!mobile"
        type="button"
        class="sh-collapse"
        :class="{ 'sh-collapse--rail': rail }"
        :title="rail ? t('nav.expand') : t('nav.collapse')"
        :aria-label="rail ? t('nav.expand') : t('nav.collapse')"
        @click="toggleRail"
      >
        <v-icon size="18" class="sh-collapse__icon">chevron_left</v-icon>
      </button>
    </div>

    <!-- ── Which production ──────────────────────────────────────────────
         The context selector, beside the identity and above everything the
         production owns. It is here rather than in the sidebar because it is
         not a destination: a producer with three shows switches WITHOUT
         leaving the screen they are on — the same top sheet, the other film
         (`nav/productionScope.ts:switchProduction`).

         Drawn only inside a production; the way IN is the slate, which is a
         screen, not a dropdown. With nothing to switch to it is a plain
         label — a menu holding one item you are already on is a click that
         does nothing. -->
    <div v-if="inProduction" class="sh-context-pick">
      <!-- On a phone the menu always opens: it is also where "All productions" lives there (the drawer
           does not repeat the production). -->
      <v-menu location="bottom start" :disabled="!canSwitch && !mobile">
        <template #activator="{ props }">
          <button
            type="button"
            class="sh-context-pick__btn"
            :class="{ 'sh-context-pick__btn--static': !canSwitch }"
            :title="canSwitch ? t('nav.chooseProduction') : undefined"
            data-testid="production-switcher"
            v-bind="canSwitch || mobile ? props : {}"
          >
            <span class="sh-context-pick__label">{{ t("nav.production") }}:</span>
            <span class="sh-context-pick__name">{{ productionName }}</span>
            <v-icon v-if="canSwitch || mobile" size="14" class="sh-context-pick__chev">unfold_more</v-icon>
          </button>
        </template>
        <div class="standalone-menu">
          <button
            v-for="choice in choices"
            :key="choice.id"
            type="button"
            class="sh-row sh-context-pick__row"
            :class="{ 'sh-context-pick__row--on': choice.id === productionId }"
            @click="onChoose(choice.id)"
          >
            <v-icon size="17" class="standalone-menu__row-icon">{{ iconFor(choice.kind) }}</v-icon>
            <span class="sh-row__label">{{ choice.title }}</span>
            <v-icon v-if="choice.id === productionId" size="15">check</v-icon>
          </button>
          <template v-if="mobile && wayOut">
            <div class="standalone-menu__divider"></div>
            <button type="button" class="sh-row" data-testid="all-productions" @click="onAllProductions">
              <v-icon size="17" class="standalone-menu__row-icon">arrow_back</v-icon>
              <span class="sh-row__label">{{ t("nav.allProductions") }}</span>
            </button>
          </template>
        </div>
      </v-menu>
    </div>

    <!-- ── Module context ────────────────────────────────────────────────
         Beside the production, the hosted module's own context — the budget
         VERSION on the costs screens — teleported here by the module. It is the
         same kind of thing as the production picker (which of several, without
         leaving the screen), so it sits in the same row rather than on the
         page. Same contract as `#pinksy-topbar-actions`: no element by this
         id means another host, and the module renders the control inline. -->
    <div id="pinksy-topbar-context" class="sh-context-slot"></div>

    <v-spacer />

    <!-- ── Module actions ────────────────────────────────────────────────
         The right of the bar belongs to the HOSTED MODULE, not the shell: a
         module teleports its own bar-level controls into this target. The
         account controls — organisation, theme, language, user — are the
         sidebar's footer. The id is a published contract: a module that
         finds no element by this id is in another host and renders its
         controls inline instead. -->
    <div id="pinksy-topbar-actions" class="sh-actions"></div>
  </v-app-bar>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import { navigateToUrl } from "single-spa";
import { useSessionStore } from "@/stores/session";
import { useProductionStore } from "@/stores/production";
import { appScopedItems, insideProduction, wayOutOfProduction } from "@/nav/navSections";
import { switchProduction } from "@/nav/productionScope";
import { productionFullName, productionIcon } from "@/production/identity";
import LogoTile from "./LogoTile.vue";
import { newVersionReady } from "@/pwa/newVersion";
import { navOpen, rail, toggleNav, toggleRail } from "./navState";
import { APP_BRANDING, APP_HOME_PATH, APP_NAME } from "@/app";

export default defineComponent({
  name: "TopBar",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const { t } = useI18n();
    // `mobileBreakpoint: 768` — plugins/vuetify.ts.
    const { mobile } = useDisplay();

    const branding = APP_BRANDING;
    /**
     * An app with its OWN identity (every WHITE_LABEL app, and a PRODUCT app
     * whose branding says so — both launch tenants) stands under its own name
     * and mark alone and never shows ours. Otherwise the bar wears the vendor
     * name with the app as the qualifier.
     */
    // 🔴 One app, and it stands under its own name (D118). This was `hasOwnIdentity(kind, branding)`
    // over a resolved tenant: a WHITE_LABEL app drew the customer's name with no mention of Pinksy, a
    // PRODUCT app drew "Pinksy" with the app's own name as a subtitle. Pinksy IS the product, so the
    // name is the brand and there is no vendor line beneath it.
    const brandName = APP_NAME;

    // ── The production context ────────────────────────────────────────
    const production = useProductionStore();
    const inProduction = computed(() => insideProduction(session.navItems, production.id));
    /**
     * 🔴 "Tuiskoms — Season 1" (D143). The bar has ONE line and it is the only place that says which
     * production you are in, so inside a season it has to carry the show as well as the season —
     * "Season 1" alone is the same label on every show ever made.
     */
    const productionName = computed(() => {
      const identity = production.identity;
      return (identity === null ? null : productionFullName(identity)) ?? production.title ?? t("nav.thisProduction");
    });
    const choices = computed(() => production.choices);
    /** One choice, and it is the one you are on, is not a choice. */
    const canSwitch = computed(() => choices.value.some((choice) => choice.id !== production.id));

    /** The way out of a production — the same destination the desktop drawer's "← All productions" uses. */
    const wayOut = computed(() => wayOutOfProduction(appScopedItems(session.navItems), APP_HOME_PATH));
    function onAllProductions(): void {
      if (wayOut.value !== null) navigateToUrl(wayOut.value);
    }

    function onChoose(productionId: string): void {
      if (productionId === production.id) return;
      // The same screen, the other production. The URL is the context, so this is all that "switch"
      // means — the store re-reads it on the routing event this fires.
      navigateToUrl(switchProduction(window.location.pathname + window.location.search, productionId));
    }

    return {
      t,
      mobile,
      newVersionReady,
      wayOut,
      onAllProductions,
      inProduction,
      productionName,
      productionId: computed(() => production.id),
      choices,
      canSwitch,
      onChoose,
      iconFor: productionIcon,
      rail,
      navOpen,
      toggleRail,
      toggleNav,
      branding,
      brandName,
    };
  },
});
</script>

<style scoped>
/* Full-bleed: the brand block at the left sits ABOVE the sidebar rather
   than beside it, so the bar does not indent by the nav width (and does not
   re-flow every time the rail collapses). */
.standalone-topbar {
  position: relative;
  background: var(--sh-chrome-bg) !important;
  /* An inset shadow, NOT `border-bottom`: a border adds a 49th pixel, and the
     bar's height is a published contract (`--pinksy-header-height`, 48px)
     that every hosted module pads itself by. A 1px drift there puts the top of
     each module's content under the bar. */
  box-shadow: inset 0 -1px 0 var(--sh-hairline) !important;
}
.standalone-topbar :deep(.v-toolbar__content) {
  position: relative;
  z-index: 1;
  padding-left: 10px;
  padding-right: 8px;
  gap: 6px;
}
/* A whisper of the tenant's own colour behind the brand block — the bar
   is tinted, not painted, so a branded navbar colour never fights the
   module below it. Drifts very slowly so the chrome feels alive. */
.standalone-topbar::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0; /* below the toolbar content, which Vuetify positions itself */
  pointer-events: none;
  background:
    radial-gradient(60% 140% at 0% 50%, rgba(var(--v-theme-primary), 0.14), transparent 70%),
    radial-gradient(30% 120% at 28% 50%, rgba(var(--v-theme-secondary), 0.08), transparent 70%);
  animation: sh-aurora 14s ease-in-out infinite alternate;
}
@keyframes sh-aurora {
  from {
    opacity: 0.7;
    transform: translateX(0);
  }
  to {
    opacity: 1;
    transform: translateX(24px);
  }
}

/* ── Brand ─────────────────────────────────────────────────────────────── */
.sh-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 0 1 auto;
}
/* The mark lifts a touch under the pointer — the brand is a thing you can
   hold, not a static stamp. */
.sh-brand__mark {
  display: inline-flex;
  flex: 0 0 auto;
  border-radius: 8px;
  transition:
    transform var(--sh-motion-slow) var(--sh-ease-spring),
    box-shadow var(--sh-motion) ease;
}
.sh-brand:hover .sh-brand__mark {
  transform: translateY(-1px) rotate(-3deg) scale(1.04);
  box-shadow: 0 6px 16px -8px rgba(var(--v-theme-primary), 0.7);
}
.sh-brand__id {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sh-brand__name {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The product qualifier beside the brand — a hairline keeps the two readable
   as "Pinksy" AND "Desk" rather than one run-on name. */
.sh-brand__sub {
  flex: 0 1 auto;
  min-width: 0;
  padding-left: 8px;
  border-left: 1px solid var(--sh-hairline);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--sh-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── The production context selector ───────────────────────────────────
   Reads as a STATEMENT of where you are ("Production: Wilder Hearts") that
   happens to be clickable — not as a form control competing with the brand
   beside it. A hairline separates it from the identity the same way the
   product qualifier is separated. */
.sh-context-pick {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-left: 4px;
  padding-left: 10px;
  border-left: 1px solid var(--sh-hairline);
}
.sh-context-pick__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 340px;
  min-width: 0;
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  cursor: pointer;
  transition: background var(--sh-motion) ease;
}
.sh-context-pick__btn:hover {
  background: var(--sh-hover);
}
.sh-context-pick__btn--static {
  cursor: default;
}
.sh-context-pick__btn--static:hover {
  background: transparent;
}
.sh-context-pick__label {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--sh-text-45);
}
.sh-context-pick__name {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-context-pick__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
}
.sh-context-pick__row--on .sh-row__label {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* The phone's "new version" pip on the nav toggle.
   🔴 PNK-196: `.sh-collapse` used to head this rule — 57dc480d slid the pip's rule in between it and the
   `.sh-nav-toggle` rule it belongs to — so on a desk the sidebar's collapse chevron was drawn as an 8px pink dot pinned
   to the top-right of the whole bar, its glyph spilling past the window's edge beside the search. */
.sh-nav-toggle__pip {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 2px var(--sh-surface, rgb(var(--v-theme-surface)));
}
.sh-collapse,
.sh-nav-toggle {
  position: relative;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  cursor: pointer;
  color: var(--sh-text-45);
  transition:
    background 150ms ease,
    color 150ms ease;
}
.sh-nav-toggle {
  /* The primary way into navigation on a phone — full-strength ink, and a
     touch target rather than a 26px chevron. */
  width: 36px;
  height: 36px;
  margin-left: -4px;
  color: var(--sh-text);
}
.sh-collapse:hover,
.sh-nav-toggle:hover {
  background: var(--sh-hover);
  color: var(--sh-text);
}
.sh-collapse:active,
.sh-nav-toggle:active {
  transform: scale(0.92);
}
.sh-collapse__icon {
  transition: transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-collapse--rail .sh-collapse__icon {
  transform: rotate(180deg);
}
/* The chevron leans the way the click will move things. */
.sh-collapse:hover .sh-collapse__icon {
  transform: translateX(-2px);
}
.sh-collapse--rail:hover .sh-collapse__icon {
  transform: rotate(180deg) translateX(-2px);
}

/* ── Module actions ────────────────────────────────────────────────────
   Whatever a module teleports in inherits the bar's ink; a module's own
   theme tokens are not defined out here (its <v-app> is elsewhere in the
   document), so an `rgb(var(--v-theme-…))` colour in the module falls
   back to this inherited value rather than to black. */
.sh-context-slot {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-left: 4px;
  padding-left: 10px;
  border-left: 1px solid var(--sh-hairline);
}
.sh-context-slot:empty {
  display: none;
}
.sh-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 2px;
  color: var(--sh-text-60);
}
.sh-actions:empty {
  display: none;
}

/* ── Narrow screens ───────────────────────────────────────────────────── */
@media (max-width: 767.98px) {
  .standalone-topbar :deep(.v-toolbar__content) {
    padding-left: 8px;
    padding-right: 4px;
    gap: 2px;
  }
}
@media (max-width: 767.98px) {
  /* On a phone the selector keeps the NAME and drops the eyebrow: "Production:"
     is a label the context block in the drawer already carries. */
  .sh-context-pick {
    margin-left: 2px;
    padding-left: 6px;
  }
  .sh-context-pick__label {
    display: none;
  }
}
@media (max-width: 420px) {
  /* Below this the product qualifier and the brand word compete for the same
     ~90px. The MARK is the identity at this size; the name follows it. */
  .sh-brand__sub {
    display: none;
  }
  .sh-brand__name {
    max-width: 96px;
  }
}
/*
 * 🔴 A THUMB'S SIZE (hardening, WCAG 2.5.8): on a touch screen the nav toggle and the production picker
 * are 44px tall inside the 48px bar, and the production's name reads at 14px.
 */
@media (pointer: coarse) {
  .sh-nav-toggle {
    width: 44px;
    height: 44px;
  }
  .sh-context-pick__btn {
    height: 44px;
  }
  .sh-context-pick__name {
    font-size: 14px;
  }
}
</style>
